"use client"

import * as Accordion from "@radix-ui/react-accordion"
import { useEffect, useState } from "react"
import { ChevronDownMini } from "@medusajs/icons"
import { sdk } from "@lib/config"
import { HttpTypes } from "@medusajs/types"
import clsx from "clsx"

type OptionsPickerProps = {
  selectedValueIds: string[]
  setOptionValueIds: (valueIds: string[]) => void
}

const OptionsPicker = ({
  selectedValueIds,
  setOptionValueIds,
}: OptionsPickerProps) => {
  const [options, setOptions] = useState<HttpTypes.StoreProductOption[]>([])
  const [openItems, setOpenItems] = useState<string[]>([])

  useEffect(() => {
    let active = true

    const fetchOptions = async () => {
      try {
        const response = await sdk.client.fetch<{
          product_options?: HttpTypes.StoreProductOption[]
        }>("/store/product-options", {
          method: "GET",
          query: {
            is_exclusive: false,
            fields: "*values",
          },
        })

        if (active && response?.product_options) {
          setOptions(
            response.product_options.filter(
              (option) =>
                option.title &&
                !option.title.toLowerCase().includes("default option") &&
                option.values?.some((value) => value.value)
            )
          )
        }
      } catch (error) {
        console.error("Failed to fetch product options", error)
      }
    }

    fetchOptions()

    return () => {
      active = false
    }
  }, [])

  if (!options.length) {
    return null
  }

  return (
    <div className="w-full min-w-0">
      <div className="hidden small:flex items-center justify-between px-1 mb-1">
        <span className="txt-compact-small-plus text-ui-fg-subtle">
          Filtrar por
        </span>
      </div>

      <Accordion.Root
        type="single"
        collapsible
        value={openItems[0] || ""}
        onValueChange={(value) => setOpenItems(value ? [value] : [])}
        className="flex w-full min-w-0 flex-col gap-2 small:gap-y-1"
      >
        <div className="flex w-full min-w-0 flex-nowrap items-center gap-2 overflow-x-auto pb-2 small:flex-col small:items-stretch small:gap-y-1 small:overflow-visible small:pb-0">
          {options.map((option) => {
            const values =
              option.values
                ?.map((value) => ({
                  id: value.id,
                  label: value.value,
                }))
                .filter(
                  (value): value is { id: string; label: string } =>
                    !!value.id &&
                    !!value.label &&
                    value.label.toLowerCase() !== "default option value"
                ) || []

            if (!values.length) {
              return null
            }

            const selectedCount = values.filter((value) =>
              selectedValueIds.includes(value.id)
            ).length

            const isOpen = openItems[0] === option.id

            const toggleValue = (valueId: string) => {
              const isSelected = selectedValueIds.includes(valueId)

              const nextSelections = isSelected
                ? selectedValueIds.filter((id) => id !== valueId)
                : [...selectedValueIds, valueId]

              setOptionValueIds(Array.from(new Set(nextSelections)))
            }

            return (
              <Accordion.Item
                key={option.id}
                value={option.id}
                className="w-auto min-w-0 shrink-0 small:w-full small:shrink"
              >
                <Accordion.Header>
                  <Accordion.Trigger
                    className={clsx(
                      "flex min-h-10 items-center justify-between gap-2 rounded-full border border-ui-border-base bg-white px-3 py-2 text-left text-small-regular",
                      "data-[state=open]:border-ui-fg-base",
                      "small:min-h-0 small:w-full small:rounded-none small:border-0 small:border-b small:bg-transparent small:px-0 small:py-3"
                    )}
                  >
                    <span className="whitespace-nowrap text-ui-fg-base">
                      {option.title}
                      {selectedCount > 0 ? ` (${selectedCount})` : ""}
                    </span>

                    <span
                      className={clsx(
                        "flex h-4 w-4 shrink-0 items-center justify-center text-ui-fg-muted transition-transform duration-150",
                        isOpen && "rotate-180"
                      )}
                    >
                      <ChevronDownMini />
                    </span>
                  </Accordion.Trigger>
                </Accordion.Header>

                <Accordion.Content
                  className={clsx(
                    "w-full min-w-0 overflow-hidden rounded-lg border border-ui-border-base bg-white data-[state=open]:mt-2 data-[state=open]:p-3 data-[state=open]:shadow-lg",
                    "data-[state=closed]:hidden",
                    "small:mt-0 small:rounded-none small:border-0 small:bg-transparent small:p-0 small:shadow-none"
                  )}
                >
                  <div className="flex flex-wrap gap-2 py-1 small:py-3">
                    {values.map((value) => {
                      const isSelected = selectedValueIds.includes(value.id)

                      return (
                        <button
                          key={value.id}
                          type="button"
                          onClick={() => toggleValue(value.id)}
                          className={clsx(
                            "flex min-h-10 items-center rounded-md border px-3 py-2 text-small-regular transition-colors",
                            isSelected
                              ? "border-ui-fg-base bg-ui-bg-subtle text-ui-fg-base"
                              : "border-ui-border-base text-ui-fg-muted hover:text-ui-fg-base"
                          )}
                          aria-pressed={isSelected}
                        >
                          {value.label}
                        </button>
                      )
                    })}
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            )
          })}
        </div>
      </Accordion.Root>
    </div>
  )
}

export default OptionsPicker
